import type { Rule } from 'antd/es/form';
import type { TFunction } from 'i18next';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { BirthDateType } from '../BirthDatePicker';
import { isDateOfBirthValid } from '../utils';

const isEmptyDate = (value?: Partial<BirthDateType>) =>
  value === undefined || (value.year === undefined && value.month === undefined && value.day === undefined);

const getValidationErrorMessage = (t: TFunction, value: Partial<BirthDateType>, isRequired: boolean) => {
  if (value.month === undefined) {
    return isRequired
      ? t('Error: Select month')
      : t('Error: Please select a month or clear the other Date of Birth fields');
  }

  if (value.day === undefined) {
    return isRequired
      ? t('Error: Select day')
      : t('Error: Please select a day or clear the other Date of Birth fields');
  }

  if (value.year === undefined) {
    return isRequired
      ? t('Error: Select year')
      : t('Error: Please select a year or clear the other Date of Birth fields');
  }

  if (!value.isNumberOfDaysValid) {
    return t('Error: Number of days in month is not valid');
  }

  return t('Error: Date of birth is not valid');
};

export const useBirthDateValidation = () => {
  const { t } = useTranslation(['translationReactComponents']);

  return useCallback(
    ({ isRequired }: { isRequired?: boolean }): Rule => ({
      validateTrigger: 'onSubmit',
      async validator(_: unknown, value?: Partial<BirthDateType>) {
        if (!isRequired && isEmptyDate(value)) {
          // eslint-disable-next-line unicorn/no-useless-promise-resolve-reject -- required for async validator
          return Promise.resolve();
        }

        if (value !== undefined && !isDateOfBirthValid(value)) {
          throw new Error(getValidationErrorMessage(t, value, isRequired ?? false));
        }
      },
    }),
    [t],
  );
};
